import { Lock, EyeOff, ShieldCheck, UserCheck, Database, Mail } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const SECTIONS = [
  {
    icon: Database,
    title: "Les données que nous collectons",
    body: [
      "Pour le compte de l'élève : un pseudonyme ou prénom, le niveau scolaire (du CP à la Terminale) et la progression dans les exercices. Aucune donnée sensible n'est collectée.",
      "Pour le compte du parent ou de l'enseignant : une adresse e-mail de contact et, le cas échéant, les informations de facturation.",
      "Nous ne collectons pas de données de localisation, pas de données de santé et nous n'accédons pas au contenu personnel du téléphone.",
    ],
  },
  {
    icon: UserCheck,
    title: "Enfants et consentement parental",
    body: [
      "Chouette Apprentissage est destinée aux élèves du CP à la Terminale. Conformément à l'article 45 de la loi Informatique et Libertés, le compte d'un élève de moins de 15 ans est créé avec l'accord du titulaire de l'autorité parentale.",
      "L'application est conçue pour les enfants : aucune publicité, aucun lien externe, aucune collecte inutile.",
      "Les fonctions sociales (amis, défis, messagerie) ne s'activent qu'après confirmation de l'adresse e-mail et vérification de l'identité d'un représentant légal (pièce d'identité + selfie). Les amis sont approuvés par un parent et chaque message est analysé par un gardien automatique ; les messages bloqués restent visibles par les parents.",
      "Les représentants légaux peuvent à tout moment demander la consultation ou la suppression des données de leur enfant via notre page Support.",
    ],
  },
  {
    icon: EyeOff,
    title: "Pas de publicité, pas de revente",
    body: [
      "Nous n'affichons aucune publicité dans l'application et sur le site.",
      "Nous ne vendons, ne louons et ne partageons vos données avec aucun tiers à des fins commerciales ou publicitaires.",
    ],
  },
  {
    icon: ShieldCheck,
    title: "Conservation et sécurité",
    body: [
      "Les données sont conservées le temps nécessaire à la fourniture du service, puis supprimées ou anonymisées. Elles sont hébergées en France.",
      "Nous mettons en œuvre des mesures techniques et organisationnelles adaptées pour protéger les données contre tout accès non autorisé.",
    ],
  },
  {
    icon: Lock,
    title: "Vos droits",
    body: [
      "Conformément au RGPD, vous disposez de droits d'accès, de rectification, d'effacement, de limitation, d'opposition et de portabilité de vos données.",
      "Vous pouvez exercer ces droits par e-mail ou via la page Support. Vous pouvez également introduire une réclamation auprès de la CNIL.",
    ],
  },
];

export default function Confidentialite() {
  return (
    <main className="pt-28 pb-24">
      <div className="max-w-3xl mx-auto px-6">
        <Reveal><span className="eyebrow">Confidentialité</span></Reveal>
        <Reveal delay={0.05}>
          <h1 className="mt-3 font-serif text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight" data-testid="privacy-title">
            Politique de <span className="italic text-coral">confidentialité</span>
          </h1>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-5 text-lg text-ink/65 leading-relaxed" data-testid="privacy-intro">
            La protection des données des enfants n'est pas une option : c'est notre fondation. Cette page explique simplement quelles données sont collectées, pourquoi, et quels sont vos droits.
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-3 text-xs font-mono uppercase tracking-[0.15em] text-ink/50" data-testid="privacy-updated">
            Dernière mise à jour : janvier 2026
          </p>
        </Reveal>

        <div className="mt-14 space-y-10">
          {SECTIONS.map((s, i) => (
            <Reveal key={i} delay={0.05}>
              <section className="card-paper p-6 sm:p-8" data-testid={`privacy-section-${i}`}>
                <div className="flex items-center gap-3.5">
                  <div className="w-10 h-10 rounded-xl bg-chalk text-paper flex items-center justify-center shrink-0">
                    <s.icon className="w-5 h-5" />
                  </div>
                  <h2 className="font-serif text-xl sm:text-2xl font-bold" data-testid={`privacy-heading-${i}`}>
                    {i + 1}. {s.title}
                  </h2>
                </div>
                <div className="mt-4 space-y-3">
                  {s.body.map((p, j) => (
                    <p key={j} className="text-sm sm:text-base text-ink/70 leading-relaxed">{p}</p>
                  ))}
                </div>
              </section>
            </Reveal>
          ))}

          <Reveal delay={0.05}>
            <section className="rounded-2xl bg-chalk text-paper p-6 sm:p-8" data-testid="privacy-contact">
              <div className="flex items-center gap-3.5">
                <div className="w-10 h-10 rounded-xl bg-paper/10 flex items-center justify-center shrink-0">
                  <Mail className="w-5 h-5 text-hi" />
                </div>
                <h2 className="font-serif text-xl sm:text-2xl font-bold" data-testid="privacy-contact-title">9. Nous contacter</h2>
              </div>
              <p className="mt-4 text-sm sm:text-base text-paper/80 leading-relaxed">
                Pour toute question relative à cette politique ou pour exercer vos droits :{" "}
                <a href="mailto:support@chouetteapprentissage.com" className="font-bold text-hi underline" data-testid="privacy-contact-mail">
                  support@chouetteapprentissage.com
                </a>
                , ou via notre <a href="/support" className="font-bold text-hi underline" data-testid="privacy-contact-support">page Support</a>.
              </p>
              <p className="mt-3 text-xs text-paper/50">
                Site édité à l'adresse chouetteapprentissage.com —{" "}
                <a href="/cgu" className="underline text-hi" data-testid="privacy-cgu-link">Conditions générales d'utilisation</a>.
              </p>
            </section>
          </Reveal>
        </div>
      </div>
    </main>
  );
}